/**
 * Medcom (GGOP outbox): after 'Indlæs kvittering (kun admin)' the row must show the new
 * status and history at once, without a page reload. The outbox query is refetched; this
 * puts the imported receipt on the cached row before and after that refetch, so the badge
 * and history update in place even if the refetch is slow or does not list the receipt yet.
 * Idempotent: data whose history already holds the receipt is returned unchanged.
 * Pure: no I/O.
 */
import type { OutboxData } from '@/components/patient/referralOutboxData';
import { knownRefuseCode, type ReceiptHistoryItem, type ReceiptStatus } from './referralReceipt';

export type ImportedReceipt = {
  /** Communicate's id of the imported receipt. */
  id: string;
  status: ReceiptStatus;
  refuseCode?: string | null;
  refuseText?: string | null;
  /** When it was imported, ISO. */
  receivedAt: string;
};

const KIND: Record<ReceiptStatus, ReceiptHistoryItem['kind']> = {
  xctl01: 'XCTL01',
  xctl02: 'XCTL02',
  xctl03: 'XCTL03',
};

export function applyImportedReceipt(
  data: OutboxData | undefined,
  rowId: string,
  receipt: ImportedReceipt,
): OutboxData | undefined {
  if (!data || !data.rows.some((row) => row.id === rowId)) return data;
  const history = data.receiptHistoryById[rowId] ?? [];
  if (history.some((item) => item.id === receipt.id)) return data;
  const positive = receipt.status === 'xctl03';
  const refuseCode = positive ? null : knownRefuseCode(receipt.refuseCode ?? null);
  const refuseText = positive ? null : (receipt.refuseText ?? null);
  const item: ReceiptHistoryItem = {
    id: receipt.id,
    kind: KIND[receipt.status],
    status: receipt.status,
    receivedAt: receipt.receivedAt,
    refuseCode,
    refuseText,
    current: true,
  };
  const refuseTextById = { ...data.refuseTextById };
  if (refuseText) refuseTextById[rowId] = refuseText;
  else delete refuseTextById[rowId];
  return {
    ...data,
    rows: data.rows.map((row) =>
      row.id === rowId ? { ...row, status: receipt.status, refuse_code: positive ? null : (receipt.refuseCode ?? null) } : row,
    ),
    refuseTextById,
    receiptReceivedAtById: { ...data.receiptReceivedAtById, [rowId]: receipt.receivedAt },
    receiptHistoryById: {
      ...data.receiptHistoryById,
      [rowId]: [item, ...history.map((h) => ({ ...h, current: false }))],
    },
  };
}
